import { useState } from 'react';

import { AUTH_STEP } from 'components/auth/AuthManager/constants';
import { useAuthManagerContext } from 'components/auth/AuthManager/Context/useAuthManagerContext';
import { axiosRestApi } from 'config/axiosRestApi';
import {
  AUTH_PENDING_ACTION_ENUM,
  TAuthPendingActionEnum,
} from 'constants/authentication';
import { useFeedbackModal, useSession, useSessionExpiryModal } from 'providers';
import { SESSION_STATE } from 'providers/SessionProvider/sessionProvider.constants';
import { useNavigate } from 'react-router-dom';
import { logError } from 'utils/logError';
import { ILoginValues } from '../types';
import { AWAITING_VALIDATION_FEEDBACK } from './constants';
import { useAuthModalError } from './useAuthModalError';

export const useExpiredSessionLogin = () => {
  const [loading, setLoading] = useState(false);

  const { isModalOpen, closeSessionExpiryModal } = useSessionExpiryModal();
  const { displayAuthErrorModal } = useAuthModalError();
  const { openFeedbackModal } = useFeedbackModal();
  const { setPhone, setStep, prefilledEmail, setPrefilledEmail } =
    useAuthManagerContext();
  const navigate = useNavigate();

  const { updateFirebaseId, updateExpiresIn, setSessionState, sessionState } =
    useSession();

  const onSubmit = async (values: ILoginValues) => {
    const { email, password } = values;
    setLoading(true);
    try {
      const { data } = await axiosRestApi.post<{
        status: string;
        expirationDate: number;
        firebaseId: string;
        phoneWithCallingCode?: string;
        pendingAction?: TAuthPendingActionEnum;
      }>('/login', {
        email,
        password,
      });

      updateExpiresIn(data.expirationDate.toString());
      updateFirebaseId(data.firebaseId);
      if (data.pendingAction) {
        setSessionState((prevSessionState) =>
          prevSessionState === SESSION_STATE.EXPIRED
            ? SESSION_STATE.EXPIRED_LOGIN_PENDING_ACTION
            : SESSION_STATE.LOGIN_PENDING_ACTION,
        );
      }
      switch (data.pendingAction) {
        case AUTH_PENDING_ACTION_ENUM.AWAITING_VALIDATION:
          openFeedbackModal(AWAITING_VALIDATION_FEEDBACK);
          break;
        case AUTH_PENDING_ACTION_ENUM.PHONE_VERIFICATION:
          setStep(AUTH_STEP.PHONE_VERIFICATION);
          break;
        case AUTH_PENDING_ACTION_ENUM.EMAIL_VERIFICATION:
          setStep(AUTH_STEP.EMAIL_VERIFICATION);
          break;
        case AUTH_PENDING_ACTION_ENUM.SECOND_FACTOR:
          if (data.phoneWithCallingCode) {
            setPhone({ phoneWithCallingCode: data.phoneWithCallingCode });
          }
          setStep(AUTH_STEP.TWO_FACTOR_AUTH);
          break;
        default:
          setSessionState(SESSION_STATE.AUTHENTICATED);
          closeSessionExpiryModal();
          break;
      }
      setPrefilledEmail(email);
    } catch (error) {
      displayAuthErrorModal(error);
      logError({ error });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setStep(AUTH_STEP.FORGOT_PASSWORD);
  };

  const handleChangeAccount = () => {
    setSessionState(SESSION_STATE.UNAUTHENTICATED);
    closeSessionExpiryModal();
    navigate('/');
  };

  return {
    loading,
    formHandleSubmit: onSubmit,
    handleForgotPassword,
    open: isModalOpen,
    prefilledEmail,
    handleChangeAccount,
    sessionState,
    setPrefilledEmail,
  };
};
